import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { ShieldCheck, Scissors } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      const to = loc.state?.from?.pathname || "/painel";
      nav(to, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao autenticar");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      {/* Left: brand panel */}
      <div className="hidden md:flex flex-col justify-between bg-slate-900 text-white p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1D4ED8 0%, transparent 50%), radial-gradient(circle at 80% 80%, #047857 0%, transparent 50%)" }} />
        <div className="relative">
          <img src="/brand/apex-logo.png" alt="APEX MEDICAL HUB" className="h-12 w-auto object-contain brightness-0 invert" />
        </div>
        <div className="relative space-y-6">
          <h1 className="font-display text-4xl font-bold leading-tight">
            Gestão da Produção Cirúrgica<br />
            <span className="text-emerald-400">BV Serviços Médicos</span>
          </h1>
          <p className="text-slate-300 text-base leading-relaxed max-w-md">
            Do mapa hospitalar ao faturamento SUS: filtragem automática por cirurgião,
            documentos exatos, auditoria completa e divergências controladas.
          </p>
          <div className="flex items-center gap-2 text-slate-400 text-sm">
            <ShieldCheck className="h-4 w-4" />
            <span>Sistema interno · Dados criptografados</span>
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} BV Serviços Médicos</div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="md:hidden flex items-center gap-2">
            <img src="/brand/apex-symbol.png" alt="Apex" className="h-8 w-auto object-contain" />
            <span className="font-display font-bold text-lg text-slate-900">Apex Medical Hub</span>
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Acessar sistema</h2>
            <p className="text-sm text-slate-500 mt-1">Entre com seu e-mail e senha corporativos.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                data-testid="input-email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@bvservicos.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required
                data-testid="input-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-700 hover:bg-blue-800" data-testid="btn-login">
            {loading ? "Entrando..." : "Entrar"}
          </Button>
          <div className="text-xs text-slate-400 text-center">
            Problemas para acessar? Contate o administrador.
          </div>
        </form>
      </div>
    </div>
  );
}
